export interface BianhuaUser {
  id: string;
  email: string;
  displayName: string;
  workEnabled: boolean;
}

interface SessionResponse {
  user: BianhuaUser;
  expiresAt: string;
  ticket?: string;
}

interface AuthErrorBody {
  error?: { code?: string; message?: string };
}

const AUTH_FLOWER_URL = new URL("../../assets/bianhua-flower-transparent.png", import.meta.url).href;
export const AUTH_REQUEST_TIMEOUT_MS = 15_000;

export interface AuthGateOptions {
  authUrl?: string;
  gatewayUrl?: string;
  testBypass?: boolean;
}

export function shouldClearStoredAuthTicket(status: number): boolean {
  return status === 401 || status === 403;
}

export async function fetchAuthRequest(
  input: RequestInfo | URL,
  init: RequestInit,
  options: { timeoutMs?: number; fetchImpl?: typeof fetch } = {},
): Promise<Response> {
  const controller = new AbortController();
  const callerSignal = init.signal;
  const abortFromCaller = () => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) abortFromCaller();
  else callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
  const timeout = globalThis.setTimeout(() => controller.abort(), options.timeoutMs ?? AUTH_REQUEST_TIMEOUT_MS);
  try {
    return await (options.fetchImpl ?? fetch)(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted && !callerSignal?.aborted) {
      throw new Error("账户服务请求超时，请检查网络后重试", { cause: error });
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeout);
    callerSignal?.removeEventListener("abort", abortFromCaller);
  }
}

export function validateEmail(value: string): string | undefined {
  const email = value.trim().toLowerCase();
  if (!email) return "请输入邮箱地址";
  if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "请输入有效的邮箱地址";
  return undefined;
}

export function validatePassword(value: string, registering = false): string | undefined {
  if (!value) return "请输入密码";
  if (registering && (value.length < 12 || !/[A-Za-z]/.test(value) || !/\d/.test(value))) {
    return "密码至少 12 位，并同时包含字母和数字";
  }
  return undefined;
}

function clientPlatform(): "web" | "windows" | "android" {
  if (window.BianhuaClientPlatform === "android") return "android";
  if (window.BianhuaClientPlatform === "windows" || window.__TAURI_INTERNALS__) return "windows";
  return "web";
}

class BianhuaAuthGate {
  private readonly authUrl: string;
  private readonly gatewayUrl: string | undefined;
  private readonly platform = clientPlatform();
  private session: SessionResponse | undefined;
  private overlay: HTMLElement | undefined;
  private ticket = "";
  private readonly ticketVault = new AuthTicketVault();

  constructor(options: AuthGateOptions) {
    this.authUrl = String(options.authUrl || "").replace(/\/$/, "");
    this.gatewayUrl = options.gatewayUrl;
  }

  async requireSession(): Promise<BianhuaUser> {
    document.documentElement.dataset.authState = "checking";
    let connectionError = "";
    try {
      this.ticket = await this.ticketVault.load();
      const existing = await this.request("/v1/session", { method: "GET" }, true);
      if (existing.ok) {
        await this.acceptSession(await existing.json() as SessionResponse);
        return this.session!.user;
      }
      if (this.ticket && shouldClearStoredAuthTicket(existing.status)) {
        await this.ticketVault.clear();
        this.ticket = "";
      } else if (!shouldClearStoredAuthTicket(existing.status)) {
        connectionError = existing.status >= 500
          ? "账户服务暂时不可用；已保留本机登录凭据，请稍后重试。"
          : `账户服务暂时无法确认登录状态（HTTP ${existing.status}）；已保留本机登录凭据。`;
      }
    } catch (error) {
      connectionError = error instanceof Error && error.message.includes("超时")
        ? error.message
        : "暂时无法连接账户服务，请检查网络后在下方重试。";
    }
    document.documentElement.dataset.authState = "required";
    return await new Promise<BianhuaUser>((resolve) => this.showAuth(resolve, connectionError));
  }

  openAccount(): void {
    if (!this.session) return;
    const dialog = document.createElement("dialog");
    dialog.className = "bianhua-account-dialog";
    const title = document.createElement("h2");
    title.textContent = "彼岸花账户";
    const identity = document.createElement("div");
    identity.className = "bianhua-account-identity";
    identity.innerHTML = `<strong></strong><span></span>`;
    identity.querySelector("strong")!.textContent = this.session.user.displayName;
    identity.querySelector("span")!.textContent = this.session.user.email;
    const status = document.createElement("p");
    status.className = "bianhua-account-status";
    status.textContent = this.session.user.workEnabled
      ? "工作区权限已启用"
      : "账户已登录，工作区权限等待服务器授权";
    const actions = document.createElement("div");
    actions.className = "bianhua-account-actions";
    const close = document.createElement("button");
    close.type = "button";
    close.textContent = "关闭";
    const logout = document.createElement("button");
    logout.type = "button";
    logout.className = "danger";
    logout.textContent = "退出登录";
    actions.append(close, logout);
    dialog.append(title, identity, status, actions);
    document.body.append(dialog);
    close.addEventListener("click", () => dialog.close());
    dialog.addEventListener("close", () => dialog.remove(), { once: true });
    logout.addEventListener("click", async () => {
      logout.disabled = true;
      await this.logout();
    });
    dialog.showModal();
    close.focus();
  }

  private async request(path: string, init: RequestInit, allowUnauthorized = false): Promise<Response> {
    if (!this.authUrl) {
      if (allowUnauthorized) return new Response(null, { status: 401 });
      throw new Error("客户端尚未配置认证服务器");
    }
    const headers = new Headers(init.headers);
    headers.set("X-Bianhua-Client-Platform", this.platform);
    if (this.platform !== "web") headers.set("X-Bianhua-Client", "1");
    if (init.body) headers.set("Content-Type", "application/json");
    if (this.platform !== "web" && this.ticket) headers.set("Authorization", `Bearer ${this.ticket}`);
    const response = await fetchAuthRequest(`${this.authUrl}${path}`, {
      ...init,
      headers,
      credentials: "include",
      cache: "no-store",
    });
    if (!allowUnauthorized && !response.ok) {
      const body = await response.json().catch(() => ({})) as AuthErrorBody;
      throw new Error(body.error?.message || "请求失败，请稍后重试");
    }
    return response;
  }

  private async acceptSession(session: SessionResponse): Promise<void> {
    if (this.platform !== "web") {
      if (!session.ticket) throw new Error("服务器没有返回客户端登录凭据");
      if (session.ticket !== this.ticket) await this.ticketVault.save(session.ticket);
      this.ticket = session.ticket;
    }
    this.session = session;
    window.BianhuaRuntimeConfig = {
      ...window.BianhuaRuntimeConfig,
      ...(this.gatewayUrl ? { gatewayUrl: this.gatewayUrl } : {}),
      authMode: this.platform === "web" ? "cookie" : "ticket",
      ...(this.platform !== "web" && this.ticket ? { authToken: this.ticket } : {}),
    };
    document.documentElement.dataset.authState = "authenticated";
    const profile = document.querySelector<HTMLElement>("[data-app-action='account']");
    const avatar = profile?.querySelector<HTMLElement>(".avatar");
    const name = profile?.querySelector<HTMLElement>("strong");
    const detail = profile?.querySelector<HTMLElement>("small");
    if (avatar) avatar.textContent = session.user.displayName.trim().slice(0, 1).toUpperCase() || "彼";
    if (name) name.textContent = session.user.displayName;
    if (detail) detail.textContent = session.user.workEnabled ? "工作区已启用" : "工作区待授权";
    this.overlay?.remove();
    this.overlay = undefined;
    document.dispatchEvent(new CustomEvent("bianhua:auth-ready", { detail: { user: session.user } }));
  }

  async logout(): Promise<void> {
    await this.request("/v1/logout", { method: "POST", body: "{}" }, true).catch(() => undefined);
    await this.ticketVault.clear();
    this.ticket = "";
    location.reload();
  }

  private showAuth(resolve: (user: BianhuaUser) => void, initialError = ""): void {
    const overlay = document.createElement("section");
    overlay.className = "bianhua-auth-gate";
    overlay.setAttribute("aria-labelledby", "bianhua-auth-title");
    overlay.innerHTML = `
      <div class="bianhua-auth-card">
        <header class="bianhua-auth-brand">
          <img src="${AUTH_FLOWER_URL}" width="52" height="52" alt="">
          <div><span>BIANHUA INTELLIGENCE</span><strong>彼岸花·智体</strong></div>
        </header>
        <div class="bianhua-auth-heading"><p>安全进入客户端</p><h1 id="bianhua-auth-title">登录彼岸花账户</h1><span>登录后才会建立远程会话。你的密码不会发送到 OpenAI。</span></div>
        <div class="bianhua-auth-tabs" role="tablist" aria-label="账户操作">
          <button type="button" role="tab" aria-selected="true" data-auth-tab="login">登录</button>
          <button type="button" role="tab" aria-selected="false" data-auth-tab="register">注册</button>
        </div>
        <form class="bianhua-auth-form" novalidate>
          <div class="bianhua-auth-field" data-register-only hidden>
            <label for="bianhua-display-name">显示名称</label>
            <input id="bianhua-display-name" name="displayName" autocomplete="name" maxlength="48" placeholder="你的称呼">
            <small data-field-error="displayName"></small>
          </div>
          <div class="bianhua-auth-field">
            <label for="bianhua-email">邮箱</label>
            <input id="bianhua-email" name="email" type="email" inputmode="email" autocomplete="email" required placeholder="name@example.com">
            <small data-field-error="email"></small>
          </div>
          <div class="bianhua-auth-field">
            <label for="bianhua-password">密码</label>
            <div class="bianhua-password-row"><input id="bianhua-password" name="password" type="password" autocomplete="current-password" required><button type="button" data-password-toggle aria-label="显示密码">显示</button></div>
            <small data-field-error="password"></small>
          </div>
          <p class="bianhua-auth-notice" data-register-note hidden>注册后账户立即可登录；工作区需服务器单独授权，避免账号之间共享模型权限。</p>
          <div class="bianhua-auth-error" role="alert" aria-live="assertive"></div>
          <button class="bianhua-auth-submit" type="submit">登录并进入</button>
        </form>
        <footer>会话使用短期凭据；客户端不保存你的 ChatGPT 密码。</footer>
      </div>`;
    document.body.append(overlay);
    this.overlay = overlay;
    const form = overlay.querySelector<HTMLFormElement>("form")!;
    const email = form.elements.namedItem("email") as HTMLInputElement;
    const password = form.elements.namedItem("password") as HTMLInputElement;
    const displayName = form.elements.namedItem("displayName") as HTMLInputElement;
    const submit = overlay.querySelector<HTMLButtonElement>(".bianhua-auth-submit")!;
    const generalError = overlay.querySelector<HTMLElement>(".bianhua-auth-error")!;
    generalError.textContent = initialError;
    let mode: "login" | "register" = "login";

    const setMode = (next: "login" | "register") => {
      mode = next;
      for (const button of overlay.querySelectorAll<HTMLButtonElement>("[data-auth-tab]")) {
        button.setAttribute("aria-selected", String(button.dataset.authTab === mode));
      }
      for (const element of overlay.querySelectorAll<HTMLElement>("[data-register-only], [data-register-note]")) element.hidden = mode !== "register";
      password.autocomplete = mode === "register" ? "new-password" : "current-password";
      submit.textContent = mode === "register" ? "创建账户并进入" : "登录并进入";
      overlay.querySelector("h1")!.textContent = mode === "register" ? "创建彼岸花账户" : "登录彼岸花账户";
      generalError.textContent = "";
    };
    for (const tab of overlay.querySelectorAll<HTMLButtonElement>("[data-auth-tab]")) tab.addEventListener("click", () => setMode(tab.dataset.authTab as "login" | "register"));
    overlay.querySelector<HTMLButtonElement>("[data-password-toggle]")!.addEventListener("click", (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const reveal = password.type === "password";
      password.type = reveal ? "text" : "password";
      button.textContent = reveal ? "隐藏" : "显示";
      button.setAttribute("aria-label", reveal ? "隐藏密码" : "显示密码");
    });

    const showFieldError = (name: string, message?: string) => {
      const error = overlay.querySelector<HTMLElement>(`[data-field-error='${name}']`)!;
      error.textContent = message || "";
      const input = form.elements.namedItem(name) as HTMLInputElement;
      input.setAttribute("aria-invalid", String(Boolean(message)));
    };
    email.addEventListener("blur", () => showFieldError("email", validateEmail(email.value)));
    password.addEventListener("blur", () => showFieldError("password", validatePassword(password.value, mode === "register")));
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const emailError = validateEmail(email.value);
      const passwordError = validatePassword(password.value, mode === "register");
      const nameError = mode === "register" && displayName.value.trim().length < 2 ? "显示名称至少 2 个字符" : undefined;
      showFieldError("email", emailError);
      showFieldError("password", passwordError);
      showFieldError("displayName", nameError);
      if (emailError || passwordError || nameError) {
        (nameError ? displayName : emailError ? email : password).focus();
        return;
      }
      submit.disabled = true;
      submit.textContent = mode === "register" ? "正在创建账户…" : "正在登录…";
      generalError.textContent = "";
      try {
        const payload: Record<string, string> = { email: email.value.trim().toLowerCase(), password: password.value };
        if (mode === "register") payload.displayName = displayName.value.trim();
        const response = await this.request(`/v1/${mode}`, { method: "POST", body: JSON.stringify(payload) });
        await this.acceptSession(await response.json() as SessionResponse);
        resolve(this.session!.user);
      } catch (error) {
        generalError.textContent = error instanceof Error ? error.message : "请求失败，请稍后重试";
        submit.disabled = false;
        submit.textContent = mode === "register" ? "创建账户并进入" : "登录并进入";
      }
    });
    email.focus();
  }
}

export async function requireBianhuaSession(options: AuthGateOptions): Promise<BianhuaUser> {
  if (options.testBypass) {
    const user = { id: "test-user", email: "test@bianhua.invalid", displayName: "测试账户", workEnabled: true };
    document.documentElement.dataset.authState = "authenticated";
    window.BianhuaAuth = { user, openAccount() {}, logout: async () => {} };
    return user;
  }
  const gate = new BianhuaAuthGate(options);
  const user = await gate.requireSession();
  window.BianhuaAuth = {
    user,
    openAccount: () => gate.openAccount(),
    logout: async () => {
      await gate.logout();
    },
  };
  return user;
}
import { AuthTicketVault } from "./auth-ticket";
